import React, { useState, useRef } from 'react';
import {
  FileSpreadsheet,
  Check,
  AlertTriangle,
  AlertCircle,
  Refresh,
  X,
  ChevronDown,
  ChevronRight,
  Folder,
  Users,
  Plus,
  Download,
} from '../../components/common/Icons';
import { Button } from '../../components/common/Button';
import importApi, {
  PreviewResponse,
  ExecuteResponse,
  TimesheetPreviewResponse,
  TimesheetExecuteResponse,
} from '../../api/importApi';
import './DataImport.css';

type Step = 'upload' | 'preview' | 'executing' | 'done';
type ImportMode = 'clients' | 'timesheets';

const TYPE_LABELS: Record<string, string> = {
  adm: 'ADM',
  tnm: 'T&M',
  staff_aug: 'Staff Aug',
  inhouse: 'Inhouse',
  digital_marketing: 'Digital Mktg',
  upwork: 'Upwork',
  outsource: 'Outsource',
};

export function DataImport() {
  const [importMode, setImportMode] = useState<ImportMode>('clients');
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<Step>('upload');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [timesheetPreview, setTimesheetPreview] = useState<TimesheetPreviewResponse | null>(null);
  const [result, setResult] = useState<ExecuteResponse | null>(null);
  const [timesheetResult, setTimesheetResult] = useState<TimesheetExecuteResponse | null>(null);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState<any>(null);  // Full error response for debugging
  const [showDebugLog, setShowDebugLog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showWarnings, setShowWarnings] = useState(false);
  const [showClients, setShowClients] = useState(false);
  const [showMissingEmployees, setShowMissingEmployees] = useState(false);
  const [showMissingProjects, setShowMissingProjects] = useState(false);
  const [updateProjects, setUpdateProjects] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setError('');
      setStep('upload');
      setPreview(null);
      setTimesheetPreview(null);
      setResult(null);
      setTimesheetResult(null);
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      if (importMode === 'clients') {
        const data = await importApi.preview(file);
        setPreview(data);
      } else {
        // Always show full preview
        const data = await importApi.timesheetPreview(file);
        setTimesheetPreview(data);
      }
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!file) return;
    setStep('executing');
    setError('');
    setErrorDetails(null);
    try {
      if (importMode === 'clients') {
        const data = await importApi.execute(file);
        setResult(data);
        setStep('done');
      } else {
        // Always use regular import (duplicates are updated)
        const data = await importApi.timesheetExecute(file, { updateProjects });
        setTimesheetResult(data);
        setStep('done');
      }
    } catch (err: any) {
      setError(err.message || 'Import failed');
      // Capture full error response for debugging
      if (err.response) {
        setErrorDetails(err.response);
      }
      setStep('preview');
    }
  };

  const reset = () => {
    setFile(null);
    setStep('upload');
    setPreview(null);
    setTimesheetPreview(null);
    setResult(null);
    setTimesheetResult(null);
    setError('');
    setErrorDetails(null);
    setShowDebugLog(false);
    setShowWarnings(false);
    setShowClients(false);
    setShowMissingEmployees(false);
    setShowMissingProjects(false);
    setUpdateProjects(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleModeChange = (mode: ImportMode) => {
    if (mode !== importMode) {
      reset();
      setImportMode(mode);
    }
  };

  return (
    <div className="di-container">
      <div className="di-header">
        <div className="di-header-left">
          <FileSpreadsheet />
          <h1>Data Import</h1>
        </div>
        <p className="di-subtitle">
          Import data from old system CSV export
        </p>
      </div>

      {/* Mode Toggle */}
      <div className="di-mode-toggle">
        <button
          className={`di-mode-btn ${importMode === 'clients' ? 'active' : ''}`}
          onClick={() => handleModeChange('clients')}
        >
          <Folder /> Clients & Projects
        </button>
        <button
          className={`di-mode-btn ${importMode === 'timesheets' ? 'active' : ''}`}
          onClick={() => handleModeChange('timesheets')}
        >
          <Users /> Timesheets
        </button>
      </div>

      <div className="di-panel">
        <div className="di-panel-header">
          <h2>{importMode === 'clients' ? 'Import Clients & Projects' : 'Import Timesheets'}</h2>
          <p>
            {importMode === 'clients'
              ? 'Upload your CSV file. Clients are created from the "Account" column, projects from all mapped fields.'
              : 'Upload your timesheet CSV file. Employee lookup by email/code/name. Duplicates will be overwritten.'}
          </p>
          <p className="di-hint">
            {importMode === 'clients'
              ? 'This will replace all existing clients & projects data.'
              : 'Employees and projects must exist. Missing task types will be created automatically.'}
          </p>
        </div>

        {/* Step indicators */}
        <div className="di-steps">
          <div className={`di-step ${step === 'upload' ? 'active' : 'completed'}`}>
            <span className="di-step-num">1</span> Upload CSV
          </div>
          <div className="di-step-arrow">&rarr;</div>
          <div className={`di-step ${step === 'preview' ? 'active' : step === 'executing' || step === 'done' ? 'completed' : ''}`}>
            <span className="di-step-num">2</span> Review Preview
          </div>
          <div className="di-step-arrow">&rarr;</div>
          <div className={`di-step ${step === 'done' ? 'active completed' : step === 'executing' ? 'active' : ''}`}>
            <span className="di-step-num">3</span> {step === 'executing' ? 'Importing...' : 'Done'}
          </div>
        </div>

        {/* Upload area */}
        {step === 'upload' && (
          <div className="di-upload-area">
            <button
              className="di-download-template"
              onClick={() => importMode === 'clients' ? importApi.downloadTemplate() : importApi.downloadTimesheetTemplate()}
            >
              <Download /> Download CSV Template
            </button>
            <div className="di-dropzone" onClick={() => fileRef.current?.click()}>
              <Plus />
              <p>{file ? file.name : 'Click to select CSV file'}</p>
              <span className="di-dropzone-hint">Accepted format: .csv</span>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
            
            {/* Show preview button for all files */}
            {file && (
              <div className="di-upload-actions">
                <Button onClick={handlePreview} disabled={loading}>
                  {loading ? <><Refresh /> Analyzing...</> : 'Preview Import'}
                </Button>
                <button className="di-btn-text" onClick={reset}>
                  <X /> Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="di-error-section">
            <div className="di-error">
              <AlertCircle /> {error}
            </div>
            
            {/* Detailed error info for debugging */}
            {errorDetails && (
              <div className="di-debug-section">
                {/* Error type and partial results */}
                {errorDetails.error_type && (
                  <div className="di-debug-info">
                    <strong>Error Type:</strong> {errorDetails.error_type}
                  </div>
                )}
                
                {errorDetails.partial_results && (
                  <div className="di-debug-info">
                    <strong>Partial Results:</strong> Created: {errorDetails.partial_results.created}, 
                    Updated: {errorDetails.partial_results.updated}, 
                    Skipped: {errorDetails.partial_results.skipped}
                  </div>
                )}
                
                {/* Debug Log Toggle */}
                {errorDetails.debug_log && errorDetails.debug_log.length > 0 && (
                  <div className="di-section">
                    <button 
                      className="di-section-toggle warn" 
                      onClick={() => setShowDebugLog(!showDebugLog)}
                    >
                      {showDebugLog ? <ChevronDown /> : <ChevronRight />}
                      <AlertTriangle /> Debug Log ({errorDetails.debug_log.length} entries)
                    </button>
                    {showDebugLog && (
                      <div className="di-debug-log">
                        {errorDetails.debug_log.map((entry: string, i: number) => (
                          <div key={i} className="di-debug-entry">{entry}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Traceback Toggle */}
                {errorDetails.traceback && (
                  <div className="di-section">
                    <details className="di-traceback">
                      <summary className="di-traceback-toggle">
                        <AlertCircle /> Full Traceback (click to expand)
                      </summary>
                      <pre className="di-traceback-content">{errorDetails.traceback}</pre>
                    </details>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Preview */}
        {step === 'preview' && importMode === 'clients' && preview && (
          <div className="di-preview">
            {/* Summary cards */}
            <div className="di-summary-cards">
              <div className="di-summary-card info">
                <div className="di-summary-value">{preview.total_rows}</div>
                <div className="di-summary-label">Total Projects</div>
              </div>
              <div className="di-summary-card success">
                <div className="di-summary-value">{preview.clients.length}</div>
                <div className="di-summary-label">Clients ({preview.clients_new} new)</div>
              </div>
              <div className="di-summary-card warning">
                <div className="di-summary-value">{preview.managers_not_found.length}</div>
                <div className="di-summary-label">Managers Not Found</div>
              </div>
              <div className="di-summary-card danger">
                <div className="di-summary-value">{preview.warnings_count}</div>
                <div className="di-summary-label">Warnings</div>
              </div>
            </div>

            {/* Unmatched managers */}
            {preview.managers_not_found.length > 0 && (
              <div className="di-info-box warn">
                <h4><AlertTriangle /> Managers not found in system</h4>
                <div className="di-tag-list">
                  {preview.managers_not_found.map((m, i) => (
                    <span key={i} className="di-tag warn">{m}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Unmatched sales */}
            {preview.sales_not_found.length > 0 && (
              <div className="di-info-box warn">
                <h4><AlertTriangle /> Sales person codes not found</h4>
                <div className="di-tag-list">
                  {preview.sales_not_found.map((s, i) => (
                    <span key={i} className="di-tag warn">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Clients collapsible */}
            <div className="di-section">
              <button className="di-section-toggle" onClick={() => setShowClients(!showClients)}>
                {showClients ? <ChevronDown /> : <ChevronRight />}
                <Folder /> Clients ({preview.clients.length})
              </button>
              {showClients && (
                <div className="di-table-wrap">
                  <table className="di-table">
                    <thead>
                      <tr>
                        <th>Client Name</th>
                        <th className="di-cell-num">Projects</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.clients.map((c, i) => (
                        <tr key={i}>
                          <td>{c.name}</td>
                          <td className="di-cell-num">{c.project_count}</td>
                          <td>
                            <span className={`di-badge ${c.is_new ? 'new' : 'ok'}`}>
                              {c.is_new ? 'NEW' : 'EXISTS'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Warnings collapsible */}
            {preview.warnings_count > 0 && (
              <div className="di-section">
                <button className="di-section-toggle warn" onClick={() => setShowWarnings(!showWarnings)}>
                  {showWarnings ? <ChevronDown /> : <ChevronRight />}
                  <AlertTriangle /> Warnings ({preview.warnings_count})
                </button>
                {showWarnings && (
                  <div className="di-warnings-list">
                    {preview.warnings.map((w, i) => (
                      <div key={i} className="di-warning-item">{w}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Projects table */}
            <div className="di-table-wrap">
              <table className="di-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Project Name</th>
                    <th>Code</th>
                    <th>Client</th>
                    <th>Type</th>
                    <th>Manager</th>
                    <th>Sales</th>
                    <th>Status</th>
                    <th>Billable</th>
                    <th>Start</th>
                    <th>End</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.projects.slice(0, 100).map((p) => (
                    <tr key={p.row} className={p.warnings.length > 0 ? 'row-warn' : ''}>
                      <td>{p.row}</td>
                      <td className="di-cell-name" title={p.project_name}>{p.project_name}</td>
                      <td>{p.project_code || <span className="di-muted">—</span>}</td>
                      <td>
                        {p.client_name}
                        {p.is_new_client && <span className="di-badge new">NEW</span>}
                      </td>
                      <td>
                        {p.project_type ? (
                          <span className={`di-type-badge ${p.project_type}`}>
                            {TYPE_LABELS[p.project_type] || p.project_type}
                          </span>
                        ) : (
                          <span className="di-muted">—</span>
                        )}
                      </td>
                      <td>
                        {p.manager_name ? (
                          <>
                            {p.manager_name}
                            {!p.manager_found && <span className="di-badge danger">?</span>}
                          </>
                        ) : (
                          <span className="di-muted">—</span>
                        )}
                      </td>
                      <td>
                        {p.sales_code ? (
                          <>
                            {p.sales_name || p.sales_code}
                            {!p.sales_found && <span className="di-badge danger">?</span>}
                          </>
                        ) : (
                          <span className="di-muted">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`di-status-badge ${p.status}`}>{p.status}</span>
                      </td>
                      <td>{p.is_billable ? '✓' : '—'}</td>
                      <td>{p.start_date || <span className="di-muted">—</span>}</td>
                      <td>{p.end_date || <span className="di-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.projects.length > 100 && (
                <div className="di-table-more">
                  Showing 100 of {preview.projects.length} rows
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="di-preview-actions">
              <Button onClick={handleExecute}>
                <Check /> Import {preview.total_rows} Projects into {preview.clients.length} Clients
              </Button>
              <button className="di-btn-text" onClick={reset}>
                <X /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* Timesheet Preview */}
        {step === 'preview' && importMode === 'timesheets' && timesheetPreview && (
          <div className="di-preview">
            {/* Preview limit notice */}
            {timesheetPreview.rows_previewed < timesheetPreview.total_rows && (
              <div className="di-info-box info" style={{ marginBottom: '16px' }}>
                <strong>Large file detected:</strong> Previewing first {timesheetPreview.rows_previewed.toLocaleString()} of {timesheetPreview.total_rows.toLocaleString()} rows.
                All rows will be imported when you proceed.
              </div>
            )}

            {/* Summary cards */}
            <div className="di-summary-cards">
              <div className="di-summary-card info">
                <div className="di-summary-value">{timesheetPreview.total_rows.toLocaleString()}</div>
                <div className="di-summary-label">Total Rows</div>
              </div>
              <div className="di-summary-card success">
                <div className="di-summary-value">{timesheetPreview.valid_rows.toLocaleString()}</div>
                <div className="di-summary-label">Valid (of previewed)</div>
              </div>
              <div className="di-summary-card warning">
                <div className="di-summary-value">{timesheetPreview.duplicates_to_update.toLocaleString()}</div>
                <div className="di-summary-label">Duplicates (will update)</div>
              </div>
              <div className="di-summary-card danger">
                <div className="di-summary-value">{timesheetPreview.invalid_rows.toLocaleString()}</div>
                <div className="di-summary-label">Invalid (of previewed)</div>
              </div>
            </div>

            {/* Missing employees */}
            {timesheetPreview.employees_missing.length > 0 && (
              <div className="di-section">
                <button className="di-section-toggle warn" onClick={() => setShowMissingEmployees(!showMissingEmployees)}>
                  {showMissingEmployees ? <ChevronDown /> : <ChevronRight />}
                  <AlertTriangle /> Employees Not Found ({timesheetPreview.employees_missing.length})
                </button>
                {showMissingEmployees && (
                  <div className="di-tag-list">
                    {timesheetPreview.employees_missing.map((e: string, i: number) => (
                      <span key={i} className="di-tag warn">{e}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Missing projects */}
            {timesheetPreview.projects_missing.length > 0 && (
              <div className="di-section">
                <button className="di-section-toggle warn" onClick={() => setShowMissingProjects(!showMissingProjects)}>
                  {showMissingProjects ? <ChevronDown /> : <ChevronRight />}
                  <AlertTriangle /> Projects Not Found ({timesheetPreview.projects_missing.length})
                </button>
                {showMissingProjects && (
                  <div className="di-tag-list">
                    {timesheetPreview.projects_missing.map((p: string, i: number) => (
                      <span key={i} className="di-tag warn">{p}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* New task types */}
            {timesheetPreview.task_types_new.length > 0 && (
              <div className="di-info-box info">
                <h4><Plus /> New Task Types to Create</h4>
                <div className="di-tag-list">
                  {timesheetPreview.task_types_new.map((t: string, i: number) => (
                    <span key={i} className="di-tag info">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Warnings */}
            {timesheetPreview.warnings_count > 0 && (
              <div className="di-section">
                <button className="di-section-toggle warn" onClick={() => setShowWarnings(!showWarnings)}>
                  {showWarnings ? <ChevronDown /> : <ChevronRight />}
                  <AlertTriangle /> Warnings ({timesheetPreview.warnings_count})
                </button>
                {showWarnings && (
                  <div className="di-warnings-list">
                    {timesheetPreview.warnings.map((w, i) => (
                      <div key={i} className="di-warning-item">{w}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Preview table */}
            <div className="di-table-wrap">
              <table className="di-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Employee</th>
                    <th>Project</th>
                    <th>Task</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Approved By</th>
                    <th>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheetPreview.preview_rows.slice(0, 100).map((r) => (
                    <tr key={r.row} className={!r.employee_found || !r.project_found ? 'row-warn' : r.is_duplicate ? 'row-dup' : ''}>
                      <td>{r.row}</td>
                      <td>{r.date || <span className="di-muted">—</span>}</td>
                      <td>
                        {r.employee_name || r.employee_email}
                        {!r.employee_found && <span className="di-badge danger">?</span>}
                      </td>
                      <td>
                        {r.project}
                        {!r.project_found && <span className="di-badge danger">?</span>}
                      </td>
                      <td>{r.task || <span className="di-muted">—</span>}</td>
                      <td className="di-cell-num">{r.hours}</td>
                      <td>
                        <span className={`di-status-badge ${r.status}`}>{r.status}</span>
                      </td>
                      <td>
                        {r.approved_by || <span className="di-muted">—</span>}
                      </td>
                      <td>
                        {r.is_duplicate && <span className="di-badge info">DUP</span>}
                        {r.warnings.length > 0 && (
                          <span className="di-badge warn" title={r.warnings.join(', ')}>!</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {timesheetPreview.preview_rows.length > 100 && (
                <div className="di-table-more">
                  Showing 100 of {timesheetPreview.preview_rows.length} rows
                </div>
              )}
            </div>

            {/* Update projects option */}
            <div className="di-options">
              <label className="di-checkbox-label">
                <input
                  type="checkbox"
                  checked={updateProjects}
                  onChange={(e) => setUpdateProjects(e.target.checked)}
                />
                Also update project data from CSV (manager, billable status, category)
              </label>
            </div>

            {/* Actions */}
            <div className="di-preview-actions">
              <Button onClick={handleExecute} disabled={timesheetPreview.valid_rows === 0}>
                <Check /> Import All {timesheetPreview.total_rows.toLocaleString()} Timesheets
              </Button>
              <button className="di-btn-text" onClick={reset}>
                <X /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* Executing */}
        {step === 'executing' && (
          <div className="di-executing">
            <Refresh />
            {importMode === 'clients' ? (
              <p>Importing data... This will replace all existing clients & projects.</p>
            ) : (
              <p>Importing timesheets... Processing in batches of 500. This may take a while for large files.</p>
            )}
          </div>
        )}

        {/* Done - Clients */}
        {step === 'done' && importMode === 'clients' && result && (
          <div className="di-done">
            <div className="di-done-icon"><Check /></div>
            <h3>{result.message}</h3>
            <div className="di-summary-cards">
              <div className="di-summary-card success">
                <div className="di-summary-value">{result.clients_created}</div>
                <div className="di-summary-label">Clients Created</div>
              </div>
              <div className="di-summary-card success">
                <div className="di-summary-value">{result.projects_created}</div>
                <div className="di-summary-label">Projects Created</div>
              </div>
              {result.warnings_count > 0 && (
                <div className="di-summary-card warning">
                  <div className="di-summary-value">{result.warnings_count}</div>
                  <div className="di-summary-label">Warnings</div>
                </div>
              )}
            </div>
            {result.warnings_count > 0 && (
              <div className="di-section">
                <button className="di-section-toggle warn" onClick={() => setShowWarnings(!showWarnings)}>
                  {showWarnings ? <ChevronDown /> : <ChevronRight />}
                  <AlertTriangle /> Warnings ({result.warnings_count})
                </button>
                {showWarnings && (
                  <div className="di-warnings-list">
                    {result.warnings.map((w, i) => (
                      <div key={i} className="di-warning-item">{w}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="di-preview-actions">
              <Button onClick={reset}><Refresh /> Import Another File</Button>
            </div>
          </div>
        )}

        {/* Done - Timesheets */}
        {step === 'done' && importMode === 'timesheets' && timesheetResult && (
          <div className="di-done">
            <div className="di-done-icon"><Check /></div>
            <h3>{timesheetResult.message}</h3>
            <div className="di-summary-cards">
              <div className="di-summary-card success">
                <div className="di-summary-value">{timesheetResult.created}</div>
                <div className="di-summary-label">Timesheets Created</div>
              </div>
              <div className="di-summary-card info">
                <div className="di-summary-value">{timesheetResult.updated}</div>
                <div className="di-summary-label">Timesheets Updated</div>
              </div>
              <div className="di-summary-card warning">
                <div className="di-summary-value">{timesheetResult.skipped}</div>
                <div className="di-summary-label">Skipped</div>
              </div>
              {timesheetResult.projects_updated > 0 && (
                <div className="di-summary-card info">
                  <div className="di-summary-value">{timesheetResult.projects_updated}</div>
                  <div className="di-summary-label">Projects Updated</div>
                </div>
              )}
            </div>
            {timesheetResult.task_types_created_count > 0 && (
              <div className="di-info-box info">
                <h4><Plus /> Task Types Created ({timesheetResult.task_types_created_count})</h4>
                <div className="di-tag-list">
                  {timesheetResult.task_types_created.map((t, i) => (
                    <span key={i} className="di-tag info">{t}</span>
                  ))}
                </div>
              </div>
            )}
            {timesheetResult.warnings_count > 0 && (
              <div className="di-section">
                <button className="di-section-toggle warn" onClick={() => setShowWarnings(!showWarnings)}>
                  {showWarnings ? <ChevronDown /> : <ChevronRight />}
                  <AlertTriangle /> Warnings ({timesheetResult.warnings_count})
                </button>
                {showWarnings && (
                  <div className="di-warnings-list">
                    {timesheetResult.warnings.map((w, i) => (
                      <div key={i} className="di-warning-item">{w}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="di-preview-actions">
              <Button onClick={reset}><Refresh /> Import Another File</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataImport;
